import { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { PlayCircle, ExternalLink, Info, Sparkles, Wand2 } from "lucide-react";
import { buildPreviewHtml } from "../lib/browserPreview";

export default function BrowserPreviewDialog({
  open,
  onOpenChange,
  blueprint,
  spriteUploads = [],
  overrideHtml = null,
  mode = "procedural", // "procedural" | "bridge"
  loading = false,
}) {
  const [nonce, setNonce] = useState(0);

  const spriteUrls = useMemo(
    () => (spriteUploads || []).map((u) => u.url).filter(Boolean),
    [spriteUploads]
  );

  const html = useMemo(() => {
    if (overrideHtml) return overrideHtml;
    if (!blueprint) return "";
    return buildPreviewHtml(blueprint, spriteUrls);
  }, [blueprint, spriteUrls, overrideHtml, nonce]);

  useEffect(() => {
    if (open) setNonce((n) => n + 1);
  }, [open, overrideHtml]);

  const openInNewTab = () => {
    const w = window.open("", "_blank");
    if (w) {
      w.document.open();
      w.document.write(html);
      w.document.close();
    }
  };

  const isBridge = mode === "bridge";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-cream border-2 border-ink rounded-3xl max-w-4xl max-h-[92vh] overflow-y-auto"
        data-testid="browser-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
            {isBridge ? (
              <Wand2 className="w-5 h-5 text-marigold" />
            ) : (
              <PlayCircle className="w-5 h-5 text-marigold" />
            )}
            {isBridge ? "Swift → Browser Bridge" : "Preview in Browser"}
            {isBridge && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-marigold/15 text-marigold font-bold">
                AI
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-ink2">
            {isBridge ? (
              <>
                Transpiled live by Claude Sonnet 4.5 from your{" "}
                <span className="font-semibold text-ink">
                  {blueprint?.engine || "SpriteKit"}
                </span>{" "}
                Swift code. Best-effort port — SpriteKit can't run in browsers, but
                the gameplay loop should feel the same.
              </>
            ) : (
              <>
                A playable <strong>feel-preview</strong> of{" "}
                <span className="font-semibold text-ink">
                  {blueprint?.title || "your game"}
                </span>
                . Not the final {blueprint?.engine || "SpriteKit"} build — for
                that, download the Xcode starter and hit ⌘R.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div
          className="rounded-2xl overflow-hidden border-2 border-ink bg-paper relative"
          data-testid="browser-preview-frame-wrap"
        >
          {loading ? (
            <div className="w-full h-[520px] flex flex-col items-center justify-center gap-4 text-ink2">
              <div className="w-12 h-12 rounded-full border-4 border-marigold/20 border-t-marigold animate-spin" />
              <div className="text-sm font-medium">
                <Sparkles className="w-4 h-4 inline mr-1 text-marigold" />
                Transpiling SpriteKit → Canvas…
              </div>
              <div className="text-xs text-ink2/70 max-w-sm text-center">
                Claude Sonnet 4.5 is reading your Swift scene and emitting a
                browser-playable port. ~10-30 seconds.
              </div>
            </div>
          ) : (
            <iframe
              key={nonce}
              title="game-browser-preview"
              srcDoc={html}
              sandbox="allow-scripts allow-same-origin"
              className="w-full h-[520px] bg-cream"
              data-testid="browser-preview-iframe"
            />
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink2 pt-2">
          <div className="inline-flex items-start gap-2 max-w-xl">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-marigold" />
            <span>
              {isBridge
                ? "Ported by AI from your actual GameScene.swift — may not be pixel-perfect. Restart via the Start button inside the preview."
                : `Procedural HTML5 Canvas preview (genre → ${classifyLabel(
                    blueprint?.genre
                  )}). Uploaded sprites auto-appear as player/enemy/coin.`}
            </span>
          </div>
          <button
            type="button"
            onClick={openInNewTab}
            disabled={loading || !html}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream transition text-xs disabled:opacity-40"
            data-testid="browser-preview-popout-button"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open in new tab
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function classifyLabel(genre = "") {
  const g = String(genre).toLowerCase();
  if (/match|tile|block|puzzle|sudoku|bejewel|candy/.test(g)) return "puzzle";
  if (/shoot|arcade|shmup|invader|defense|tower|bullet/.test(g)) return "shooter";
  return "runner";
}
